import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { formResolver } from 'src/utils/formResolver';
import { emailScheme, stringScheme, urlZendeskArticlesScheme } from 'src/utils/validation-schemes';
import { Button } from 'src/components/ui/button';
import {
  useLazyGetKnowledgeBaseCollectorZendeskConsentLinkQuery,
  useUpdateKnowledgeBaseCollectorMutation,
} from 'src/services/knowledgeBaseService';
import { useParams } from 'react-router-dom';
import { useNotification } from 'src/hooks/useNotification';
import { useLoader } from 'src/provider/LoaderProvider';
import { INTEGRATION_NAMES_MAP } from 'src/utils/fileUtils';
import * as yup from 'yup';
import { ZendeskHelpCenterIsPrivate } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/create/CreateZendeskIntegrationPopup/ZendeskIntegrationForm2/ZendeskHelpCenterFields/ZendeskHelpCenterIsPrivate';
import { InputInfoToolTip } from 'src/components/Input/InputInfoToolTip';
import { Switch } from 'src/components/ui/switch';
import { ZendeskHelpCenterExcludedUrls } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/create/CreateZendeskIntegrationPopup/ZendeskIntegrationForm2/ZendeskHelpCenterFields/ZendeskHelpCenterExcludedUrls';
import { useAppSelector } from 'src/store';
import { ZendeskHelpCenterAddUrls } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/create/CreateZendeskIntegrationPopup/ZendeskIntegrationForm2/ZendeskHelpCenterFields/ZendeskHelpCenterAddUrls';
import { Collapsible } from 'src/components/ui/collapsible';
import { Separator } from 'src/components/ui/separator';
import { Spinner } from 'src/components/Spinner/Spinner';
import ArrowLargeIcon from 'src/assets/img/arrow-large-icon.svg';
import ChevronDownIcon from 'src/assets/img/chevron-down-icon.svg';
import type { ZendeskHelpCenterFormValues } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/create/CreateZendeskIntegrationPopup/ZendeskIntegrationForm2/zendeskHelpCenterFormValues';
import type { TreeNode } from 'src/utils/fileUtils';
import { ZendeskHelpCenterCredentialFields } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/create/CreateZendeskIntegrationPopup/ZendeskIntegrationForm2/ZendeskHelpCenterFields/ZendeskHelpCenterCredentialFields';
import { ZendeskHelpCenterBrandFields } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/create/CreateZendeskIntegrationPopup/ZendeskIntegrationForm2/ZendeskHelpCenterFields/ZendeskHelpCenterBrandFields';
import { ZendeskHelpCenterDomain } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/create/CreateZendeskIntegrationPopup/ZendeskIntegrationForm2/ZendeskHelpCenterFields/ZendeskHelpCenterDomain';
import { useLoaderWhile } from 'src/hooks/useLoaderWhile';
import { useZendeskAuthorizationPolling } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/create/CreateZendeskIntegrationPopup/ZendeskIntegrationForm2/useZendeskAuthorizationPolling';

export const EditKnowledgeBaseZendeskHelpCenterForm = ({
  knowledgeBaseId,
  item,
  close = () => {},
}: {
  // A knowledge base row, or a collector wrapped as one during onboarding; the collector is in `data`.
  item: Pick<TreeNode, 'id' | 'name' | 'type' | 'data'>;
  knowledgeBaseId?: number | string | null;
  close?: () => void;
}) => {
  const { id } = useParams();
  const notification = useNotification();
  const loader = useLoader();
  const { isInactivePlan, isTrialPlan } = useAppSelector((s) => s.planSlice);
  const [
    updateCollector,
    { isLoading: isLoadingUpdateCollector, isError: isErrorUpdateCollector },
  ] = useUpdateKnowledgeBaseCollectorMutation();
  // Separate instance so saving before "Connect with Zendesk" doesn't trigger the Save success
  // flow (notification and closing the modal).
  const [saveCollectorBeforeConnect] = useUpdateKnowledgeBaseCollectorMutation();
  const [getZendeskConsentLink] = useLazyGetKnowledgeBaseCollectorZendeskConsentLinkQuery();
  const kbId = knowledgeBaseId || id;
  const { isCheckingAuthStatus, pollZendeskAuthorization } = useZendeskAuthorizationPolling(kbId);
  // Kept in state so a completed Zendesk authorization shows without reopening the modal.
  // The row being edited; its collector (with the Zendesk auth state) is in `data`.
  const [collector, setCollector] = useState(item);
  const [isManualConnectOpen, setIsManualConnectOpen] = useState(false);
  const isZendeskAuthorized =
    collector?.data?.auth_type === 'oauth' && collector?.data?.auth_status === 'ok';
  // A collector connected with an API token shows its credentials directly, with Zendesk OAuth
  // offered as the alternative; otherwise the credentials live in the collapsible manual panel.
  const usesManualToken = collector?.data?.auth_type === 'api_token';
  const isManualConnectActive = usesManualToken || isManualConnectOpen;

  useLoaderWhile(isLoadingUpdateCollector);

  useEffect(() => {
    if (isErrorUpdateCollector) {
      reset();
    }
  }, [isErrorUpdateCollector]);

  const {
    handleSubmit,
    control,
    watch,
    reset,
    clearErrors,
    formState: { isDirty, isValid },
  } = useForm<ZendeskHelpCenterFormValues>({
    defaultValues: {
      domain: item.data.domain,
      zendesk_subdomain: item.data.zendesk_subdomain,
      zendesk_email: item.data.zendesk_email,
      zendesk_api_key: '',
      brand_id: item.data.brand_id,
      type: item.data.type,
      name: item.data.name,
      is_private: item.data.collect_private_articles ? 'private' : 'public',
      enabled: item.data.enabled,
      excluded_articles: item.data.excluded_articles || [],
      source_links: item.data.source_links || [],
    },
    resolver: formResolver(
      yup.lazy((values, { context }) => {
        const isRequired = values.is_private === 'private' || values.enabled;
        // The email is only needed when connecting manually instead of via Zendesk OAuth.
        const isManualRequired = isRequired && Boolean(context?.isManualConnectActive);

        const baseSchema = {
          is_private: stringScheme({ required: true }),
          domain: stringScheme({ required: true }).label('Zendesk Help Center Domain'),
          zendesk_subdomain: stringScheme({ required: isRequired })
            .label('Zendesk API Subdomain')
            .nullable(),
          zendesk_email: emailScheme({
            required: isManualRequired,
            allowEmpty: !isManualRequired,
          })
            .label('Zendesk Email')
            .nullable(),
          zendesk_api_key: stringScheme({ required: false }).label('Zendesk API Key'),
          brand_id: stringScheme({ required: isRequired }).label('Zendesk Brand ID').nullable(),
          excluded_articles: yup.array(urlZendeskArticlesScheme({ required: false })),
          source_links: yup.array(urlZendeskArticlesScheme({ required: false })),
        };

        return yup.object({
          ...baseSchema,
        });
      }),
    ),
    context: { isManualConnectActive },
  });

  const openZendeskConsentPage = async () => {
    const { data } = await getZendeskConsentLink({
      knowledgeBaseId: kbId,
      collectorId: collector.id,
    });
    if (!data?.authorize_url) return false;

    window.open(data.authorize_url);
    return true;
  };

  const buildBody = (formData: ZendeskHelpCenterFormValues) => {
    const data: Partial<ZendeskHelpCenterFormValues> & { collect_private_articles?: boolean } = {
      ...formData,
    };
    data.collect_private_articles = data.is_private === 'private';
    delete data.is_private;

    if (!data.zendesk_api_key) {
      delete data.zendesk_api_key;
    }

    // Credentials come from Zendesk OAuth unless the manual API token panel is used.
    if (!isManualConnectActive) {
      delete data.zendesk_api_key;
      delete data.zendesk_email;
    }

    return {
      ...data,
      name: `${INTEGRATION_NAMES_MAP[data.type]} (${data.zendesk_subdomain || data.domain})`,
    };
  };

  // Zendesk authorizes the collector as stored, so unsaved changes (e.g. the API subdomain)
  // are saved first. Resolves false when saving fails.
  const saveChangesBeforeConnect = async (formData: ZendeskHelpCenterFormValues) => {
    if (!isDirty) return true;

    loader.show();
    const { error } = await saveCollectorBeforeConnect({
      knowledgeBaseId: kbId,
      collectorId: collector.id,
      body: buildBody(formData),
    });
    loader.hide();

    if (error) return false;

    // The saved values become the new baseline, so Save is only enabled for later changes.
    reset(formData);
    return true;
  };

  const onConnectWithZendesk = async (formData: ZendeskHelpCenterFormValues) => {
    if (!(await saveChangesBeforeConnect(formData))) return;

    if (await openZendeskConsentPage()) {
      pollZendeskAuthorization(collector.id, (authorizedCollector) =>
        setCollector({ ...collector, data: authorizedCollector }),
      );
    }
  };

  // The collector is already authorized, so there is no status change to poll for.
  const onReconnectWithZendesk = async (formData: ZendeskHelpCenterFormValues) => {
    if (!(await saveChangesBeforeConnect(formData))) return;

    await openZendeskConsentPage();
  };

  // Run the same validation as submit; the connect handlers only fire for a valid form.
  const connectWithZendesk = handleSubmit(onConnectWithZendesk);
  const reconnectWithZendesk = handleSubmit(onReconnectWithZendesk);

  const submit = (data: ZendeskHelpCenterFormValues) => {
    if (isDirty && isValid) {
      updateCollector({ knowledgeBaseId: kbId, collectorId: item.id, body: buildBody(data) }).then(
        (updateCollectorResult) => {
          if (!('error' in updateCollectorResult)) {
            notification.success(`${item.name} Settings Updated Successfully.`);
            close();
          }
        },
      );
    }
  };

  const isPrivate = watch('is_private');
  const enabled = watch('enabled');
  const needsCredentials = isPrivate === 'private' || enabled;

  const zendeskConnectButton = (
    <div className={'flex flex-col gap-2'}>
      {isZendeskAuthorized && (
        <output
          className={
            'block w-full rounded-lg bg-green_150 px-8 py-2 text-center text-[13px] font-medium text-green_750'
          }
        >
          Connected successfully with Zendesk
        </output>
      )}
      <Button
        type={'button'}
        variant={'dark'}
        onClick={isZendeskAuthorized ? reconnectWithZendesk : connectWithZendesk}
        className={'w-full px-8 h-[38px]'}
      >
        {isZendeskAuthorized ? 'Reconnect with Zendesk' : 'Connect with Zendesk'}
        <img src={ArrowLargeIcon} alt={''} width={16} height={16} />
      </Button>
      <p className={'text-center text-[13px] font-medium text-tertiary'}>
        You’ll be redirected to Zendesk to authorize access.
      </p>
    </div>
  );

  const orSeparator = (
    <div className={'flex items-center gap-2'}>
      <Separator className={'flex-1 bg-border_subtle'} />
      <span className={'text-[13px] font-medium text-tertiary'}>or</span>
      <Separator className={'flex-1 bg-border_subtle'} />
    </div>
  );

  const manualCredentialFields = (
    <div className={'flex flex-col gap-2'}>
      <ZendeskHelpCenterCredentialFields control={control} />
    </div>
  );

  return (
    <form
      onSubmit={handleSubmit(submit)}
      id="edit-zendesk-integration"
      className={'h-full flex flex-col justify-between'}
    >
      {isCheckingAuthStatus && (
        <div
          className={
            'absolute flex items-center justify-center w-full h-full top-0 left-0 bg-ebony_opacity_30 backdrop-blur-sm z-50'
          }
        >
          <div
            className={
              'flex flex-col items-center gap-4 bg-light p-4 rounded-sm-5 text-sm font-semibold'
            }
          >
            <Spinner className={'!w-4 !border-[3px] !border-r-ebony_opacity_50'} />
            <p>Checking Zendesk Connection...</p>
          </div>
        </div>
      )}
      <div className="flex flex-col gap-2 max-h-[500px] overflow-auto pr-4">
        <ZendeskHelpCenterIsPrivate control={control} />
        <Controller
          control={control}
          name="enabled"
          render={({ field: { onChange, value } }) => (
            <div className={'flex items-center gap-2.5'}>
              <Switch
                type="accent"
                id="edit-zendesk-help-center-auto-sync"
                checked={value}
                onCheckedChange={onChange}
                disabled={isInactivePlan || isTrialPlan}
              />
              <label
                htmlFor="edit-zendesk-help-center-auto-sync"
                className={'text-sm font-medium text-ebony_opacity_80'}
              >
                Auto-Sync
              </label>
              <InputInfoToolTip
                infoToolTip={{
                  side: 'right',
                  description: isTrialPlan ? (
                    <>Unavailable for trial plan</>
                  ) : (
                    <>Sync new content and updates in real time</>
                  ),
                }}
              />
            </div>
          )}
        />
        <ZendeskHelpCenterDomain control={control} />
        <ZendeskHelpCenterAddUrls control={control} clearErrors={clearErrors} />
        <ZendeskHelpCenterExcludedUrls control={control} clearErrors={clearErrors} />
        {needsCredentials && (
          <>
            <ZendeskHelpCenterBrandFields control={control} />
            <div className={'flex flex-col gap-2'}>
              {usesManualToken ? (
                <>
                  {manualCredentialFields}
                  {orSeparator}
                  {zendeskConnectButton}
                </>
              ) : (
                <>
                  {zendeskConnectButton}
                  {orSeparator}
                  <Collapsible
                    open={isManualConnectOpen}
                    onOpenChange={setIsManualConnectOpen}
                    disabled={collector?.data?.auth_type === 'oauth'}
                    triggerClassName={
                      'group flex w-full items-center gap-2 rounded-lg border border-grey_300 bg-white px-3 py-2 text-sm font-medium text-grey_750 transition-colors disabled:opacity-50 enabled:hover:border-green_750 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green_750/20 data-[state=open]:border-green_750 data-[state=open]:shadow-[0_0_2px_0_rgba(96,235,139,0.2)]'
                    }
                    trigger={
                      <>
                        <span className={'flex-1 text-center'}>
                          Connect manually with API token
                        </span>
                        <img
                          src={ChevronDownIcon}
                          alt={''}
                          width={12.8}
                          height={12.8}
                          className={
                            'transition-transform duration-200 group-data-[state=open]:rotate-180'
                          }
                        />
                      </>
                    }
                    contentClassName={
                      'overflow-hidden data-[state=open]:animate-collapsible-down data-[state=closed]:animate-collapsible-up'
                    }
                  >
                    <div className={'pt-2'}>{manualCredentialFields}</div>
                  </Collapsible>
                </>
              )}
            </div>
          </>
        )}
      </div>
      <div className="flex justify-center gap-2 mt-5">
        <Button variant="tertiary" type="button" className="w-[100px]" onClick={() => close()}>
          Cancel
        </Button>
        <Button variant="dark" type="submit" className="w-[100px]" disabled={!isDirty}>
          Save
        </Button>
      </div>
    </form>
  );
};
